import Application from '@app/Application';
import ChunkErrorBoundary from '@/components/error/ChunkErrorBoundary';
import FinancePage from '@pages/FinancePage';
import ForgotPasswordPage from '@/pages/Auth/ForgotPasswordPage';
import LoginPage from '@/pages/Auth/LoginPage';
import MainPage from '@pages/MainPage';
import NotFoundPage from '@pages/NotFoundPage';
import OrgRegistrationPage from '@/pages/Auth/OrgRegistrationPage';
import PageLoadingState from '@/components/ui/PageLoadingState';
import ProfilePage from '@pages/ProfilePage';
import RegisterPage from '@/pages/Auth/RegisterPage';
import ResetPasswordPage from '@/pages/Auth/ResetPasswordPage';
import ShipmentsPage from '@pages/ShipmentsPage';
import VerifyEmailPage from '@/pages/Auth/VerifyEmailPage';
import { AppBackground } from '@/components/layout';
import { GuestRoute } from '@/components/auth/GuestRoute';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { lazy, ReactNode, Suspense } from 'react';
import { paths } from './paths';

const CalcPage = lazy(() => import('@pages/CalcPage'));
const DeliveryWarehouseDistributionPage = lazy(() => import('@pages/DeliveryWarehouse/DeliveryWarehouseDistributionPage'));
const DeliveryWarehouseProductsPage = lazy(() => import('@pages/DeliveryWarehouse/DeliveryWarehouseProductsPage'));
const FinanceOperationHistoryPage = lazy(() => import('@pages/FinancePage/FinanceOperationHistoryPage'));
const FinanceUpcomingPaymentsPage = lazy(() => import('@pages/FinancePage/FinanceUpcomingPaymentsPage'));
const OrganizationEditPage = lazy(() => import('@pages/ProfilePage/OrganizationEditPage'));
const OrganizationWarehouseCreatePage = lazy(() => import('@pages/ProfilePage/OrganizationWarehouseCreatePage'));
const OrganizationWarehousePage = lazy(() => import('@pages/ProfilePage/OrganizationWarehousePage'));
const ProfileEditPage = lazy(() => import('@pages/ProfilePage/ProfileEditPage'));
const ProfileWarehousesPage = lazy(() => import('@pages/ProfilePage/ProfileWarehousesPage'));
const ShipmentAttachmentsPage = lazy(() => import('@pages/ShipmentPage/ShipmentAttachmentsPage'));
const ShipmentBudgetPage = lazy(() => import('@pages/ShipmentPage/ShipmentBudgetPage'));
const ShipmentBudgetServicesPage = lazy(() => import('@pages/ShipmentPage/ShipmentBudgetServicesPage'));
const ShipmentCustomsClearancePage = lazy(() => import('@pages/ShipmentPage/ShipmentCustomsClearancePage'));
const ShipmentDetailsPage = lazy(() => import('@pages/ShipmentPage/ShipmentDetailsPage'));
const ShipmentHistoryPage = lazy(() => import('@pages/ShipmentPage/ShipmentHistoryPage'));
const ShipmentItemsPage = lazy(() => import('@pages/ShipmentPage/ShipmentItemsPage'));
const ShipmentMediaGalleryPage = lazy(() => import('@pages/ShipmentPage/ShipmentMediaGalleryPage'));
const ShipmentPage = lazy(() => import('@pages/ShipmentPage'));
const ShipmentReceptionPage = lazy(() => import('@pages/ShipmentPage/ShipmentReceptionPage'));
const ShipmentReceptionResolutionPage = lazy(() => import('@pages/ShipmentPage/ShipmentReceptionResolutionPage'));
const ShipmentSkuDetailPage = lazy(() => import('@pages/ShipmentPage/ShipmentSkuDetailPage'));
const ShipmentSupplierDataPage = lazy(() => import('@pages/ShipmentPage/ShipmentSupplierDataPage'));
const ShipmentSupplierDropOffPage = lazy(() => import('@pages/ShipmentPage/ShipmentSupplierDropOffPage'));
const ShipmentTimelinePage = lazy(() => import('@pages/ShipmentPage/ShipmentTimelinePage'));
const AiServiceItemsPage = lazy(() => import('@pages/AiServiceItemsPage'));
const AiServiceItemEditPage = lazy(() => import('@pages/AiServiceItemsPage/AiServiceItemEditPage'));

const renderLazyPage = (page: ReactNode) => (
  <Suspense
    fallback={(
      <PageLoadingState
        className="min-h-screen"
        label="Загрузка страницы"
      />
    )}
  >
    <ChunkErrorBoundary>
      {page}
    </ChunkErrorBoundary>
  </Suspense>
);

const routesConfig = [
  {
    path: paths.forgotPassword,
    element: <AppBackground><ForgotPasswordPage /></AppBackground>
  },
  {
    path: paths.resetPassword,
    element: <AppBackground><ResetPasswordPage /></AppBackground>
  },
  {
    path: paths.login,
    element: (
      <AppBackground>
        <GuestRoute>
          <LoginPage />
        </GuestRoute>
      </AppBackground>
    ),
  },
  {
    path: paths.register,
    element: (
      <AppBackground>
        <GuestRoute>
          <RegisterPage />
        </GuestRoute>
      </AppBackground>
    ),
  },
  {
    path: paths.registerOrganization,
    element: (
      <AppBackground>
        <ProtectedRoute>
          <OrgRegistrationPage />
        </ProtectedRoute>
      </AppBackground>
    ),
  },
  {
    path: paths.verifyEmail,
    element: (
      <AppBackground>
        <VerifyEmailPage />
      </AppBackground>
    ),
  },
  {
    path: paths.main,
    element: <Application />,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <MainPage />
          </ProtectedRoute>
        ),
      },
      {
        path: paths.shipments,
        element: (
          <ProtectedRoute>
            <ShipmentsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: paths.aiServiceItemEdit,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<AiServiceItemEditPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.aiServiceItems,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<AiServiceItemsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.shipmentDetails,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<ShipmentPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.shipmentDeliveryDetails,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<ShipmentDetailsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.shipmentSupplierData,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<ShipmentSupplierDataPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.shipmentSupplierDropOff,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<ShipmentSupplierDropOffPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.shipmentTimeline,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<ShipmentTimelinePage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.shipmentReception,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<ShipmentReceptionPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.shipmentReceptionResolution,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<ShipmentReceptionResolutionPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.shipmentReceptionSku,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<ShipmentSkuDetailPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.shipmentReceptionMedia,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<ShipmentMediaGalleryPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.shipmentHistory,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<ShipmentHistoryPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.shipmentItems,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<ShipmentItemsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.shipmentItemSku,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<ShipmentSkuDetailPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.shipmentAttachments,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<ShipmentAttachmentsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.shipmentBudget,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<ShipmentBudgetPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.shipmentBudgetServices,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<ShipmentBudgetServicesPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.shipmentCustomsClearance,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<ShipmentCustomsClearancePage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.finance,
        element: (
          <ProtectedRoute>
            <FinancePage />
          </ProtectedRoute>
        ),
      },
      {
        path: paths.financeOperationHistory,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<FinanceOperationHistoryPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.financeUpcomingPayments,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<FinanceUpcomingPaymentsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.financeNested,
        element: (
          <ProtectedRoute>
            <FinancePage />
          </ProtectedRoute>
        ),
      },
      {
        path: paths.profile,
        element: (
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        ),
      },
      {
        path: paths.profileEdit,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<ProfileEditPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.profileOrganizationEdit,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<OrganizationEditPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.profileWarehouseCreate,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<OrganizationWarehouseCreatePage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.profileWarehouse,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<OrganizationWarehousePage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.profileWarehouses,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<ProfileWarehousesPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.profileNested,
        element: (
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        ),
      },
      {
        path: paths.deliveryWarehouseDistribution,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<DeliveryWarehouseDistributionPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.deliveryWarehouseProducts,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<DeliveryWarehouseProductsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: paths.deliveryWarehouseCreate,
        element: (
          <ProtectedRoute>
            {renderLazyPage(<OrganizationWarehouseCreatePage />)}
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    path: paths.calc,
    element: (
      <AppBackground>
        {renderLazyPage(<CalcPage />)}
      </AppBackground>
    ),
  },
  {
    path: paths.notFound,
    element: (
      <AppBackground>
        <NotFoundPage />
      </AppBackground>
    ),
  },
];

export default routesConfig;